/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { injectIntl, intlShape } from 'react-intl';
import { createUseStyles } from 'react-jss';
import {
  getBraindateConversation,
  getBraindateStartTime,
} from '@e180/domain/lib/braindate/util';
import { getConversationTopic } from '@e180/domain/lib/conversation/util';
import { getUserFirstName } from '@e180/domain/lib/user/util';

import {
  postBraindateAgreeValue,
  postBraindateDisagreeValue,
  postBraindatePostponeValue,
} from 'src/shared/app/post-braindate/settings/postBraindateSettings';
import messages from 'src/shared/app/post-braindate/l10n/postBraindateL10n';
import ZIndexTakeOver from 'src/shared/app/base/component/template/component/TakeOver';
import { formatTime } from 'src/shared/app/base/date/util/dateFormatters';
import { getTimeFormat } from 'src/shared/app/base/date/util/dateUtils';
import { createDateTimeFromISO } from 'src/shared/app/new-calendar/util/dateUtils';
import TakeOver from 'src/shared/app/notification/component/TakeOver';
import TopicCard from 'src/shared/components/domain/topic/feed/TopicCard';
import { getBraindateOtherParticipant } from 'src/shared/domain/braindate/util/braindateUtil';
import { isConversationAboutGroupBraindate } from 'src/shared/domain/conversation/util/conversationUtil';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';
import OptionButton from 'src/shared/ui/component/button/OptionButton';
import Heading from 'src/shared/ui/component/text/Heading';

import type { Braindate } from '@e180/domain/lib/braindate/type';
import type { Membership } from '@e180/domain/lib/membership/type';
import type { User } from '@e180/domain/lib/user/type';

import styles from './PostBraindateFeedbackTakeOver.style';

type Props = {
  braindate: Braindate,
  selfUser: User,
  membership: Membership,
  handleClick: (value: string) => void,
  handleDismiss: () => void,
  // Self passed
  intl: intlShape,
};

const useStyles = createUseStyles(styles);

const PostBraindateFeedbackTakeOverDumb = ({
  braindate,
  selfUser,
  membership,
  handleClick,
  handleDismiss,
  intl,
}: Props): React.Node => {
  const conversation = getBraindateConversation(braindate);
  const topic = getConversationTopic(conversation);
  const isGroupBraindate = isConversationAboutGroupBraindate(conversation);

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const { braindateSingular } = useEventBraindateName();

  const startTime = getBraindateStartTime(braindate);
  const timeFormat = getTimeFormat(membership, intl.locale);
  const formattedTime = formatTime(
    // $FlowIssue
    createDateTimeFromISO(startTime),
    intl.locale,
    timeFormat,
  );

  const questionTxt = isGroupBraindate
    ? intl.formatMessage(messages.postGroupBraindateQuestion, {
        braindateSingular,
      })
    : intl.formatMessage(messages.postBraindateQuestion, {
        firstName: getUserFirstName(
          getBraindateOtherParticipant(braindate, selfUser),
        ),
        time: formattedTime,
        braindateSingular,
      });

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onClickAgree = () => {
    handleClick(postBraindateAgreeValue);
  };

  const onClickDisagree = () => {
    handleClick(postBraindateDisagreeValue);
  };

  const onClickPostpone = () => {
    handleClick(postBraindatePostponeValue);
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <ZIndexTakeOver>
      <TakeOver handleDismiss={handleDismiss}>
        <div className={classes.root}>
          <Heading layoutClass={classes.title}>
            {intl.formatMessage(messages.postBraindateTitle)}
          </Heading>
          <TopicCard topic={topic} conversation={conversation} />
          <p className={classes.subTitle}>{questionTxt}</p>
          <div className={classes.options}>
            <OptionButton
              handleClick={onClickAgree}
              layoutClass={classes.option}
            >
              {intl.formatMessage(messages.postBraindateAgree)}
            </OptionButton>
            <OptionButton
              handleClick={onClickDisagree}
              layoutClass={classes.option}
            >
              {intl.formatMessage(
                isGroupBraindate
                  ? messages.postBraindateDisagreeGroup
                  : messages.postBraindateDisagree,
              )}
            </OptionButton>
            <OptionButton
              handleClick={onClickPostpone}
              layoutClass={classes.option}
            >
              {intl.formatMessage(messages.postBraindatePostpone)}
            </OptionButton>
          </div>
        </div>
      </TakeOver>
    </ZIndexTakeOver>
  );
};

export default compose(
  React.memo,
  injectIntl,
)(PostBraindateFeedbackTakeOverDumb);
